import { computed, ref } from 'vue';
import { subDays } from 'date-fns';
import { getUnixStartOfDay, getUnixEndOfDay } from 'helpers/DateHelper';
import ReportsAPI from 'dashboard/api/reports';
import atendimentoAPI from 'dashboard/api/atendimento';

// Visão geral de uso dos produtos da org, montada só com o que já existe:
// PowerChat vem dos reports v2 (conversas/mensagens por dia + resumo com o
// período anterior) e os agentes de IA vêm da mesma fonte da página Atendimento
// (REPORTS-02) — "Canais e times" comparando por agente de IA —, para a Home e
// o relatório dizerem o mesmo número de mensagens da IA. Aqui não há fallback
// silencioso: se uma chamada falha, o bloco correspondente fica vazio e
// `failed` avisa, em vez de mostrar zero como se fosse um dado real.

export const PERIODS = [7, 30, 90];

// Data no fuso do navegador, não em UTC: é o mesmo fuso que o cliente de
// reports manda em timezone_offset, e um toISOString() jogaria o balde das
// 00:00 locais para o dia anterior em qualquer fuso a leste de Greenwich.
const pad2 = n => String(n).padStart(2, '0');
const iso = date =>
  `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;

// 'YYYY-MM-DD' → 'dd/MM' sem passar por Date(): a string do backend já está no
// fuso da conta e um parse aqui a jogaria para o dia anterior em UTC-3.
const dayLabel = isoDay => {
  const [, month, day] = isoDay.split('-');
  return `${day}/${month}`;
};

// Rollup por mês quando a janela passa de 31 dias, igual ao Analytics: 90 barras
// diárias viram uma serra ilegível de 2px.
const monthLabel = isoDay => {
  const [year, month] = isoDay.split('-');
  return `${month}/${year}`;
};

// [{timestamp, value}] (reports v2, epoch em segundos) → linha do gráfico.
const fromReportSeries = (rows, monthly) => {
  const buckets = new Map();
  rows.forEach(row => {
    const date = new Date(row.timestamp * 1000);
    const isoDay = iso(date);
    const key = monthly ? isoDay.slice(0, 7) : isoDay;
    const current = buckets.get(key) || { iso: isoDay, total: 0 };
    current.total += Number(row.value) || 0;
    buckets.set(key, current);
  });
  return [...buckets.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([, bucket]) => ({
      date: monthly ? monthLabel(bucket.iso) : dayLabel(bucket.iso),
      total: bucket.total,
    }));
};

// daily [{day, ai_messages}] (atendimento/comparar, já somado entre os agentes)
// → linha do gráfico.
const fromDailySeries = (rows, monthly) => {
  const buckets = new Map();
  rows.forEach(row => {
    const key = monthly ? row.day.slice(0, 7) : row.day;
    const current = buckets.get(key) || { iso: row.day, total: 0 };
    current.total += Number(row.ai_messages) || 0;
    buckets.set(key, current);
  });
  return [...buckets.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([, bucket]) => ({
      date: monthly ? monthLabel(bucket.iso) : dayLabel(bucket.iso),
      total: bucket.total,
    }));
};

export function useProductUsage() {
  const days = ref(30);
  const isLoading = ref(true);
  const failed = ref(false);

  const summary = ref(null);
  const conversationsTimeline = ref([]);
  const aiTimeline = ref([]);
  const aiRanking = ref([]);

  const monthly = computed(() => days.value > 31);

  const fetchAll = async () => {
    isLoading.value = true;
    failed.value = false;

    const to = new Date();
    const from = subDays(to, days.value - 1);
    const range = { from: getUnixStartOfDay(from), to: getUnixEndOfDay(to) };

    // Um produto sem dado não pode derrubar o outro: allSettled deixa o
    // PowerChat na tela mesmo quando a leitura dos agentes falha.
    const [summaryResult, conversationsResult, aiResult] =
      await Promise.allSettled([
        ReportsAPI.getSummary(range.from, range.to, 'account'),
        ReportsAPI.getReports({
          metric: 'conversations_count',
          from: range.from,
          to: range.to,
          groupBy: 'day',
        }),
        atendimentoAPI.comparar({ since: iso(from), until: iso(to) }, 'agent'),
      ]);

    failed.value = [summaryResult, conversationsResult, aiResult].some(
      result => result.status === 'rejected'
    );

    summary.value =
      summaryResult.status === 'fulfilled' ? summaryResult.value.data : null;

    conversationsTimeline.value =
      conversationsResult.status === 'fulfilled'
        ? fromReportSeries(conversationsResult.value.data, monthly.value)
        : [];

    const ai = aiResult.status === 'fulfilled' ? aiResult.value.data : null;
    aiTimeline.value = fromDailySeries(ai?.daily || [], monthly.value);

    // Ranking por agente: mensagens da IA de cada PowerAgent no período, com o
    // nome que o agente tem no OS.
    aiRanking.value = (ai?.rows || [])
      .map(agent => ({
        key: agent.key,
        label: agent.name,
        value: Number(agent.ai_messages) || 0,
      }))
      .filter(entry => entry.value > 0)
      .sort((a, b) => b.value - a.value);

    isLoading.value = false;
  };

  const aiMessages = computed(() =>
    aiTimeline.value.reduce((acc, row) => acc + row.total, 0)
  );

  const messages = computed(() => {
    if (!summary.value) return null;
    return (
      (summary.value.incoming_messages_count || 0) +
      (summary.value.outgoing_messages_count || 0)
    );
  });

  // O resumo já traz o período anterior de mesmo tamanho: a variação é dele, não
  // de uma segunda chamada.
  const delta = key => {
    const previous = summary.value?.previous?.[key];
    const current = summary.value?.[key];
    if (!previous || current === undefined || current === null) return null;
    return ((current - previous) / previous) * 100;
  };

  // Mensagens são a soma de duas métricas, então a variação sai da soma dos dois
  // valores anteriores — não dá para reaproveitar delta() de uma chave só.
  const messagesDelta = computed(() => {
    const previous = summary.value?.previous;
    if (!previous || messages.value === null) return null;
    const base =
      (previous.incoming_messages_count || 0) +
      (previous.outgoing_messages_count || 0);
    if (!base) return null;
    return ((messages.value - base) / base) * 100;
  });

  return {
    days,
    isLoading,
    failed,
    summary,
    messages,
    messagesDelta,
    aiMessages,
    conversationsTimeline,
    aiTimeline,
    aiRanking,
    delta,
    fetchAll,
  };
}
